<script>
  import { mapState } from "vuex";
  import { routeSlugs } from "@/utils/routes-translations";
  import { capitalize } from "@/utils/utils";
  import BreadCrumbs from "@/components/BreadCrumbs";
  import ArticleImage from "@/components/ArticleImage";
  import CardArticle from "@/components/CardArticle";
  import CallToAction from "@/components/CallToAction";
  import UsabillaContainer from "@/components/UsabillaContainer";
  import { HeadMeta } from "@/utils/head-meta";
  import ArticleContent from "@/components/FlexibleContent/ArticleContent.vue";

  export default {
    components: {
      BreadCrumbs,
      ArticleImage,
      CardArticle,
      CallToAction,
      UsabillaContainer,
      ArticleContent,
    },
    async asyncData ({ app, params, error }) {
      try {
        const { items } = await app.$contentful.getEntries({
          content_type: "article",
          "fields.slug": params.slug,
        });
        if (!items.length) {
          return error({
            statusCode: 404,
            message: "This page could not be found",
          });
        }

        const relatedArticles = await app.$contentful.getEntries({
          content_type: "article",
          limit: 3,
          order: "-sys.createdAt",
          "sys.id[nin]": items[0].sys.id,
          "fields.category.sys.contentType.sys.id": "category",
          "fields.category.fields.title[match]":
            items[0].fields.category.fields.title,
        });

        return {
          article: items[0],
          relatedArticles: relatedArticles.items || [],
        };
      } catch (e) {
        error(e);
      }
    },
    head () {
      return HeadMeta(this.article.fields);
    },
    computed: {
      showUsabillaWidget () {
        return (
          this.article &&
          this.article.sys &&
          this.article.sys.contentType.sys.id === "article"
        );
      },
      ...mapState({
        config: (state) => state.config,
      }),
      articleSingleCTA () {
        if (!this.config.fields || !this.config.fields.articleSingleCTA) {
          return false;
        }
        return this.config.fields.articleSingleCTA.fields;
      },
      featuredImage () {
        if (
          !this.article.fields.featuredImage ||
          this.article.fields.hideFeaturedImageOnSinglePage
        ) {
          return false;
        }
        return "https:" + this.article.fields.featuredImage.fields.file.url;
      },
      featuredImageTitle () {
        if (!this.featuredImage) return "";
        return this.article.fields.featuredImage.fields.title;
      },
      breadcrumbs () {
        const contentType = this.article.sys.contentType.sys.id;
        const crumbs = [];
        let prefix = "/";
        if (contentType === "page") {
          const parentPage = this.article.fields.parentPage;
          const grandParentPage =
            parentPage && parentPage.fields.parentPage
              ? parentPage.fields.parentPage
              : false;
          if (grandParentPage) {
            crumbs.push({
              title: grandParentPage.fields.title,
              slug: prefix + grandParentPage.fields.slug,
            });
            prefix += grandParentPage.fields.slug + "/";
          }
          if (parentPage) {
            crumbs.push({
              title: parentPage.fields.title,
              slug: prefix + parentPage.fields.slug,
            });
            prefix += parentPage.fields.slug + "/";
          }
          crumbs.push({
            title: this.article.fields.title,
            slug: prefix + this.article.fields.slug,
          });
        }
        if (contentType === "article") {
          const articlesName = routeSlugs.articles.slice(1);
          prefix += articlesName;
          crumbs.push({
            title: capitalize(articlesName),
            slug: prefix,
          });
          prefix += "/categorie/";
          crumbs.push({
            title: this.article.fields.category.fields.title,
            slug: prefix + this.article.fields.category.fields.slug,
          });
          crumbs.push({
            title: this.article.fields.title,
            slug: "/artikel/" + this.article.fields.slug,
          });
        }
        return crumbs;
      },
    },
    mounted () {
      if (this.showUsabillaWidget) {
        document.body.classList.add("show-usabilla-button");
      }
    },
    beforeDestroy () {
      document.body.classList.remove("show-usabilla-button");
    },
  };
</script>

<template>
  <article class="view view-article">
    <bread-crumbs :breadcrumbs="breadcrumbs" />
    <header class="view-header">
      <div class="md:px-8 container px-4">
        <div class="md:w-10/12 xl:w-158 w-full mx-auto">
          <h1 class="md:text-4xl md:text-center mb-4 text-3xl">
            {{ article.fields.title }}
          </h1>
          <rich-text-content
            v-if="article.fields.introduction"
            class="rich-text-content mb-8 text-lg"
            :content="article.fields.introduction"
          />
        </div>
      </div>
    </header>
    <div class="md:px-8 container px-4">
      <div class="lg:w-11/12 xl:w-158 w-full mx-auto">
        <figure
          v-if="featuredImage"
          class="md:mt-18 md:mb-20 my-10"
        >
          <article-image
            :image="featuredImage"
            :alt="featuredImageTitle"
          />
        </figure>
      </div>
    </div>
    <div class="md:px-8 container px-4">
      <div class="md:w-10/12 xl:w-158 w-full mx-auto">
        <rich-text-content :content="article.fields.content" />
        <article-content :content="article.fields.contentBlocks" />
        <usabilla-container
          v-if="showUsabillaWidget"
          ub-id="5f91a143020bac2bd2645687"
          class="mt-12"
        />
      </div>
    </div>
    <div
      v-if="relatedArticles.length > 0"
      class="related-articles md:mt-30 mt-20"
    >
      <div class="md:px-8 container px-4">
        <h2 class="md:text-center pb-10 text-3xl">
          Meer examentips
        </h2>
      </div>
      <div class="md:px-8 container px-4">
        <div class="flex flex-wrap -mx-4">
          <div
            v-for="article in relatedArticles"
            :key="article.sys.id"
            class="md:w-1/2 lg:w-1/4 w-full px-4 pb-10"
          >
            <card-article :post="article" />
          </div>
          <div
            v-if="articleSingleCTA"
            class="md:w-1/2 lg:w-1/4 w-full px-4 pb-10"
          >
            <call-to-action :fields="articleSingleCTA" />
          </div>
        </div>
        <div class="text-center">
          <e-button
            tag="nuxt-link"
            to="/examentips"
            type="tertiary"
            class="button-all-news"
          >
            Alle examentips
          </e-button>
        </div>
      </div>
    </div>
  </article>
</template>

<style lang="scss">
.view-article {
  .button-all-news {
    @apply text-blue-500;

    &:hover {
      @apply text-blue-600;
    }
  }
}
</style>
